// Imports
import {
  Component,
  Input,
  Output,
  ViewChild,
  EventEmitter,
  NgZone,
  forwardRef,
  QueryList,
  AfterViewInit,
  ContentChildren,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  ElementRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CKButtonDirective } from './ckbutton.directive';
import { CKGroupDirective } from './ckgroup.directive';
import { BaseService } from '../../shared/services/base.service';

/**
 * CKEditor component
 * Usage :
 *  <ckeditor [(ngModel)]="data" [config]="{...}" debounce="500"></ckeditor>
 */
@Component({
    selector: 'ckeditor',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CKEditorComponent),
            multi: true,
        },
    ],
    template: `<textarea #host></textarea>
  @if (isMacroShown && macroResults.length > 0) {
    <ul class="list-group macro-dropdown-menu">
      @for (data of macroResults; track data) {
        <li class="list-group-item" (click)="insertMacro(data)">
          {{ data.macro_key }} | {{ data.macro_description }}
        </li>
      }
      @if (macroResults.length === 0) {
        <li class="list-group-item">
          No Record Found
        </li>
      }
    </ul>
  }`,
    styles: [
        '.macro-dropdown-menu {position: absolute; z-index: 10000;max-height: 200px; overflow-y: auto;}'
    ],
    standalone: false
})
export class CKEditorComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() config: CKEDITOR.config;
  @Input() readonly: boolean;
  @Input() debounce: string;

  @Output() change = new EventEmitter<CKEDITOR.eventInfo>();
  @Output() editorChange = new EventEmitter<CKEDITOR.eventInfo>();
  @Output() ready = new EventEmitter<CKEDITOR.eventInfo>();
  @Output() blur = new EventEmitter<CKEDITOR.eventInfo>();
  @Output() focus = new EventEmitter<CKEDITOR.eventInfo>();
  @Output() contentDom = new EventEmitter<CKEDITOR.eventInfo>();
  @Output() fileUploadRequest = new EventEmitter<CKEDITOR.eventInfo>();
  @Output() fileUploadResponse = new EventEmitter<CKEDITOR.eventInfo>();
  @Output() paste = new EventEmitter<CKEDITOR.eventInfo>();
  @Output() drop = new EventEmitter<CKEDITOR.eventInfo>();

  @ViewChild('host', { static: false }) host: ElementRef<HTMLTextAreaElement>;

  @ContentChildren(CKButtonDirective) toolbarButtons: QueryList<CKButtonDirective>;
  @ContentChildren(CKGroupDirective) toolbarGroups: QueryList<CKGroupDirective>;

  _value = '';
  instance: CKEDITOR.editor;
  debounceTimeout: number;
  private destroyed = false;

  isMacroShown = false;
  macroResults = [];
  isMacroEnabled = false;
  macroDatasource: any;
  currentTrigger: '@' | '#';
  private macroApiSub: any;
  macroDebounceTimer: any = null;
  MACRO_DEBOUNCE_MS = 1000;
  keyupHandler: any;
  isLimitReached: boolean;

  /**
   * Constructor
   */
  constructor(private zone: NgZone, private baseService: BaseService) { }

  get value(): string {
    return this._value;
  }

  @Input()
  set value(v: string) {
    if (v !== this._value) {
      const sanitizedValue = this.sanitizeHtml(v);
      this._value = sanitizedValue;
      this.onChange(sanitizedValue);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.readonly && this.instance) {
      this.instance.setReadOnly(changes.readonly.currentValue);
    }
  }

  /**
   * On component destroy
   */
  ngOnDestroy(): void {
    this.destroyed = true;
    this.isLimitReached = false;

    if (this.instance && this.keyupHandler) {
      this.instance.document.removeListener('keyup', this.keyupHandler);
    }
    
    this.zone.runOutsideAngular(() => {
      if (this.instance) {
        //CKEDITOR.removeAllListeners();
        this.instance.destroy();
        this.instance = null;
      }
    });
  }

  /**
   * On component view init
   */
  ngAfterViewInit(): void {
    if (this.destroyed) {
      return;
    }
    this.ckeditorInit(this.config || {});
  }

  /**
   * On component view checked
   */
  ngAfterViewChecked(): void {
    this.ckeditorInit(this.config || {});
  }

  /**
   * Value update process
   */
  updateValue(value: any): void {
    this.zone.run(() => {
      this.value = this.sanitizeHtml(value);

      this.onChange(this.value);

      this.onTouched();
      this.change.emit(this.value as any);
    });
  }

  /**
   * CKEditor init
   */
  ckeditorInit(config: CKEDITOR.config): void {
    if (typeof CKEDITOR === 'undefined') {
      console.warn('CKEditor 4.x is missing (http://ckeditor.com/)');
    } else {
      // Check textarea exists
      if (this.instance || !this.documentContains(this.host.nativeElement)) {
        return;
      }

      if (this.readonly) {
        config.readOnly = this.readonly;
      }
      // CKEditor replace textarea
      this.instance = CKEDITOR.replace(this.host.nativeElement, config);

      // Set initial value
      this.instance.setData(this.sanitizeHtml(this.value));

      // listen for instanceReady event
      this.instance.on('instanceReady', (evt: CKEDITOR.eventInfo) => {
        // if value has changed while instance loading
        // update instance with current component value
        if (this.instance.getData() !== this.value) {
          const sanitizedValue = this.sanitizeHtml(this.value);
          this.instance.setData(sanitizedValue);
          this.change.emit(sanitizedValue as any);
        }

        // send the evt to the EventEmitter
        this.ready.emit(evt);
      });

      // CKEditor change event
      this.instance.on('change', (evt: CKEDITOR.eventInfo) => {

        if (this.isLimitReached) return;

        this.onTouched();

        const value = this.sanitizeHtml(this.instance.getData());
        const plainText = this.getPlainText(value);
        const maxLength = this.config?.['maxlength'];

        if (maxLength) {
          //  Within limit
          if (plainText.length <= maxLength) {

            if (this.debounce) {
              if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
              }

              this.debounceTimeout = window.setTimeout(() => {
                this.updateValue(value);
                this.debounceTimeout = null;
              }, parseInt(this.debounce));

            } else {
              this.updateValue(value);
            }

            this.isLimitReached = false;
            this.editorChange?.emit(evt);
            return;
          }
          // LIMIT EXCEEDED (FIXED)
          this.baseService['alertsService'].error(`Maximum allowed length is ${maxLength} characters.`);
          this.isLimitReached = true;

          const trimmedText = plainText.slice(0, maxLength);
          //  IMPORTANT: delay BOTH operations (fix race condition)
          setTimeout(() => {
            this.instance.setData(trimmedText);
            this.updateValue(trimmedText);
            try {
              const range = this.instance.createRange();
              range.moveToElementEditEnd(this.instance.editable());
              this.instance.getSelection().selectRanges([range]);
            } catch (e) { }
            this.isLimitReached = false;
          }, 0);
          return;
        }

        //  No maxlength
        if (this.value !== value) {

          if (this.debounce) {
            if (this.debounceTimeout) {
              clearTimeout(this.debounceTimeout);
            }

            this.debounceTimeout = window.setTimeout(() => {
              this.updateValue(value);
              this.debounceTimeout = null;
            }, parseInt(this.debounce));

          } else {
            this.updateValue(value);
          }
        }

        this.editorChange?.emit(evt);
      });

      // CKEditor blur event
      this.instance.on('blur', (evt: CKEDITOR.eventInfo) => {
        this.blur.emit(evt);
      });

      // CKEditor focus event
      this.instance.on('focus', (evt: CKEDITOR.eventInfo) => {
        this.focus.emit(evt);
      });

      // CKEditor contentDom event
      this.instance.on('contentDom', (evt: CKEDITOR.eventInfo) => {
        this.contentDom.emit(evt);
      });

      // CKEditor fileUploadRequest event
      this.instance.on('fileUploadRequest', (evt: CKEDITOR.eventInfo) => {
        this.fileUploadRequest.emit(evt);
      });

      // CKEditor fileUploadResponse event
      this.instance.on('fileUploadResponse', (evt: CKEDITOR.eventInfo) => {
        this.fileUploadResponse.emit(evt);
      });

      // CKEditor paste event
      this.instance.on('paste', (evt: CKEDITOR.eventInfo) => {
        this.paste.emit(evt);
      });

      // CKEditor drop event
      this.instance.on('drop', (evt: CKEDITOR.eventInfo) => {
        this.drop.emit(evt);
      });

      // Add Toolbar Groups to Editor. This will also add Buttons within groups.
      this.toolbarGroups.forEach((group) => {
        group.initialize(this);
      });
      // Add Toolbar Buttons to Editor.
      this.toolbarButtons.forEach((button) => {
        button.initialize(this);
      });

      this.isMacroEnabled = !!config['macro_support'];
      this.macroDatasource = config['macro_datasource'] || null;

      // Detect macro trigger text
      if (this.isMacroEnabled && this.macroDatasource) {
        this.instance.on('contentDom', () => {
          this.keyupHandler = () => {
            this.zone.run(() => {
              // this.detectMacroTrigger();
              clearTimeout(this.macroDebounceTimer);

              this.macroDebounceTimer = setTimeout(() => {
                this.detectMacroTrigger();
              }, this.MACRO_DEBOUNCE_MS);
            });
          };

          this.instance.document.on('keyup', this.keyupHandler);
        });
      }
    }
  }

  /**
   * Implements ControlValueAccessor
   */
  writeValue(value: string): void {
    const sanitizedValue = this.sanitizeHtml(value);
    this._value = sanitizedValue;
    if (this.instance) this.instance.setData(sanitizedValue);
  }

  onChange: (_: string) => void;

  onTouched: () => void;

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private documentContains(node: Node) {
    return document.contains ? document.contains(node) : document.body.contains(node);
  }

  /**
   * Sanitize HTML content to remove Word tags containing colons using DOMParser
   * and return XHTML-serialized output (preserves self-closing tags) using XMLSerializer.
   */
  private sanitizeHtml(html: string): string {
    if (!html || typeof html !== 'string') return html;

    try {
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(html, 'text/html');

      if (this.unwrapWordElements(htmlDoc.body)) {
        const xhtmlNS = 'http://www.w3.org/1999/xhtml';
        const xmlDoc = document.implementation.createDocument(xhtmlNS, 'div', null);
        const wrapper = xmlDoc.documentElement; // <div xmlns="http://www.w3.org/1999/xhtml">

        Array.from(htmlDoc.body.childNodes).forEach(node => {
          const imported = xmlDoc.importNode(node, true); // deep import
          wrapper.appendChild(imported);
        });

        const serializer = new XMLSerializer();
        let out = '';
        Array.from(wrapper.childNodes).forEach(child => {
          out += serializer.serializeToString(child);
        });

        return out.trim();
      } else {
        // If no changes were made, return original HTML
        return html;
      }
    } catch (err) {
      // If anything goes wrong, fallback to the cleaned HTML string (less strict but safe)
      console.warn('sanitizeHtml: XHTML serialization failed, returning cleaned HTML instead.', err);
      try {
        // As a safe fallback, return the cleaned HTML from the HTML DOM parse
        const fallbackParser = new DOMParser();
        const fallbackDoc = fallbackParser.parseFromString(html, 'text/html');
        if (this.unwrapWordElements(fallbackDoc.body))
          return fallbackDoc.body.innerHTML.trim();
        else
          return html; // If no changes were made, return original HTML
      } catch (err2) {
        // Last resort: return original input
        console.warn('sanitizeHtml: fallback also failed, returning original HTML.', err2);
        return html;
      }
    }
  }

  /**
   * Unwrap elements with namespaced tag names (containing colons)
   * Similar to JSoup's unwrap() method
   */
  private unwrapWordElements(element: Element): boolean {
    const elementsToUnwrap: Element[] = [];
    
    // Find all elements with colons in their tag names
    const allElements = element.querySelectorAll('*');
    allElements.forEach(el => {
      if (el.tagName.includes(':')) {
        elementsToUnwrap.push(el);
      }
    });
    
    // Unwrap the namespaced elements (preserve content, remove wrapper)
    elementsToUnwrap.forEach(el => {
      this.unwrapElement(el);
    });

    return elementsToUnwrap.length > 0;
  }

  /**
   * Unwrap an element - move its children to parent and remove the element
   * Mimics JSoup's unwrap() functionality
   */
  private unwrapElement(element: Element): void {
    const parent = element.parentNode;
    if (!parent) return;
    
    // Move all child nodes to parent before the element
    while (element.firstChild) {
      parent.insertBefore(element.firstChild, element);
    }
    
    // Remove the now-empty element
    parent.removeChild(element);
  }

  private detectMacroTrigger(): void {
    const selection = this.instance.getSelection();
    if (!selection) return;

    const range = selection.getRanges()[0];
    if (!range || !range.startContainer) return;

    const container = range.startContainer;
    const textBeforeCursor = container.getText ? container.getText().substring(0, range.startOffset) : '';

    // Detect @ or # macros
    const match = textBeforeCursor.match(/([@#])([\w]*)$/);
    if (!match) {
      this.isMacroShown = false;
      this.macroResults = [];
      return;
    }
    const triggerChar = match[1];
    const searchText = match[2];

    if (triggerChar === '@' || triggerChar === '#') {
      this.showMacroDropdown(searchText, triggerChar);
    }
  }

  private showMacroDropdown(searchText: string, triggerChar: '@' | '#') {
    this.currentTrigger = triggerChar;
    // Cancel previous API call if needed
    if (this.macroApiSub) {
      this.macroApiSub.unsubscribe();
    }
    this.hideMacroDropdown();
    const payload = { search: searchText };
    const { resource, application, query_id } = this.macroDatasource;

    this.macroApiSub = this.baseService.getData(payload, '', resource, application, query_id).subscribe((data: any) => {
      this.isMacroShown = true;
      this.macroResults = data?.resource || [];
      // this.showMacroDropdownInsideEditor(this.macroResults, this.currentRange);
    });
  }

  private hideMacroDropdown() {
    this.isMacroShown = false;
    this.macroResults = [];
  }

  insertMacro(macro: any): void {
    if (!this.instance) return;

    const editor = this.instance;
    const sel = editor.getSelection();
    if (!sel) return;

    const range = sel.getRanges()[0];
    if (!range || !range.startContainer) return;

    const node = range.startContainer;

    // Only handle simple text typing case
    if (node.type !== CKEDITOR.NODE_TEXT) return;

    const textNode = node as CKEDITOR.dom.text;
    const text = textNode.getText();
    const cursor = range.startOffset;

    // Find last @ or #
    const at = Math.max(text.lastIndexOf('@', cursor - 1), text.lastIndexOf('#', cursor - 1));

    if (at === -1) return;

    editor.fire('saveSnapshot');

    // Select from @ to cursor
    range.setStart(textNode, at);
    range.setEnd(textNode, cursor);
    sel.selectRanges([range]);

    // Replace with macro text
    editor.insertText(macro.macro_description);

    editor.fire('saveSnapshot');
    this.hideMacroDropdown();
  }


  getPlainText(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.innerText || '';
  }

}
